import { authnClient } from '@frontend/auth';
import { localStorageHelper } from '@frontend/auth-helpers';
import { Icon } from '@frontend/icons';
import { TableColumnConfig } from '@frontend/design-system';
import { useIntegrationsDashboardStore } from '../store';
import { Confirmation, UserRole, Verticals } from './constants';
import { Integration, IntegrationsMetadata, RoleBasedValue, TableData } from './typings';

const CAPITAL_SPLIT_REGEX = /([a-z])([A-Z])/g; //  Regex to find boundaries between a lowercase and an uppercase letter to split strings.
const SEPARATORS_REGEX = /[-_.]+/g; //  Regex to match common separators like hyphens, underscores, or periods.
const MULTIPLE_SPACES_REGEX = /\s+/g; //  Regex to match one or more whitespace characters.

/**
 * Cache to avoid recomputing capability strings multiple times.
 * Key is `${dataType}_${operation}`, value is { formatted, camelCase }.
 */
const capabilityCache = new Map<string, { formatted: string; camelCase: string }>();

/**
 * Normalizes a string by splitting camelCase, replacing separators with spaces, and trimming whitespace.
 * @param {string} input The string to normalize.
 * @returns {string} The normalized, space-separated string.
 */
const normalizeString = (input: string): string =>
  (input || '')
    .replace(CAPITAL_SPLIT_REGEX, '$1 $2')
    .replace(SEPARATORS_REGEX, ' ')
    .replace(MULTIPLE_SPACES_REGEX, ' ')
    .trim();

/**
 * Formats a string into Title Case, where each word is capitalized.
 * @param {string} input The string to format.
 * @returns {string} The string in Title Case format.
 */
export const formatString = (input: string): string =>
  normalizeString(input)
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

/**
 * Converts a string into camelCase format.
 * @param {string} input The string to convert.
 * @returns {string} The camelCased string.
 */
export const toCamelCase = (input: string): string =>
  normalizeString(input)
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (m, idx) => (idx === 0 ? m.toLowerCase() : m.toUpperCase()))
    .replace(MULTIPLE_SPACES_REGEX, '');

/**
 * Creates a capability object with formatted (Title Case) and camelCase versions.
 * Uses a cache to avoid recomputation.
 * @param {string} dataType The data type (e.g., 'user', 'deviceInfo').
 * @param {string} operation The operation (e.g., 'create', 'read').
 * @returns {{ formatted: string; camelCase: string }} An object containing both formatted and camelCase strings.
 */
const buildCapability = (dataType: string, operation?: string): { formatted: string; camelCase: string } => {
  const key = operation ? `${dataType}_${operation}` : dataType;
  if (capabilityCache.has(key)) {
    return capabilityCache.get(key) as { formatted: string; camelCase: string };
  }

  const combined = operation ? `${dataType} ${operation}` : dataType;
  const result = {
    formatted: formatString(combined),
    camelCase: toCamelCase(combined),
  };

  capabilityCache.set(key, result);
  return result;
};

/**
 * Replaces the last word in a header string based on the operation type.
 * - If the header ends with "List", it replaces it with "Sync".
 * - If the header ends with "Create" or "Update", it replaces it with "Writeback".
 * - Otherwise, the header is returned unchanged.
 *
 * @param {string} headerText - The input header string (e.g., "User List", "Account Create").
 * @returns {string} The modified header string with the appropriate replacement.
 */
const replaceHeaderText = (headerText: string): string => {
  const lastWord = headerText.split(' ').pop();
  const headerPrefix = headerText.substring(0, headerText.lastIndexOf(' '));

  switch (lastWord) {
    case 'List':
      return `${headerPrefix} Sync`;
    case 'Create':
    case 'Update':
      return `${headerPrefix} Writeback`;
    default:
      return headerText;
  }
};

/**
 * @constant typeColorMap
 * @description A map to associate integration connection types with specific colors for UI chips.
 */
const typeColorMap: Record<string, string> = {
  Cloud: 'primary',
  'On Prem': 'warn',
};

/**
 * Gets a color string based on the integration connection type.
 * @param {string} type The integration connection type (e.g., 'Cloud', 'On Premise').
 * @returns {string | undefined} The corresponding color string or undefined if not found.
 */
export const getTypeColor = (type: string): string | undefined => typeColorMap[buildCapability(type).formatted];

/**
 * Formats a date into a clean, readable "Mon YYYY" format.
 * @param {string | number | Date} date The date to format.
 * @returns {string} The formatted date string (e.g., "Sep 2025").
 */
export const cleanDate = (date: string | number | Date): string =>
  new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
  });

/**
 * Builds the integrations table's columns and data based on user role and selected vertical.
 * @param {IntegrationsMetadata} integrationsMetadata Metadata for integrations.
 * @param {Integration[]} integrations List of integrations.
 * @param {string} selectedVertical Currently selected vertical filter.
 * @param {UserRole} userRole The role of the current user.
 * @returns {{ integrationsTableColumns: TableColumnConfig<TableData>[]; integrationsTableData: TableData[] }} Object containing table columns and data.
 */
export const buildIntegrationsTable = (
  integrationsMetadata?: IntegrationsMetadata,
  integrations?: Integration[],
  userRole?: keyof typeof UserRole,
  selectedVertical?: string
): { integrationsTableColumns: TableColumnConfig<TableData>[]; integrationsTableData: TableData[] } => {
  const normalizedSelectedVertical = selectedVertical?.toLowerCase();
  const isAllVertical = normalizedSelectedVertical === Verticals.ALL.toLowerCase();
  const normalizedUserRole = userRole?.toLowerCase();
  const normalizedAllRole = UserRole.ALL.toLowerCase();

  const roleAccessibility = getCapabilitiesRbac(integrationsMetadata, integrations);
  const rbacAccessibleFields = normalizedUserRole ? roleAccessibility[normalizedUserRole] ?? [] : [];
  const allAccessibleFields = roleAccessibility[normalizedAllRole] || [];
  const accessibleFields =
    normalizedUserRole === normalizedAllRole
      ? Array.from(new Set(Object.values(roleAccessibility).flat()))
      : Array.from(new Set([...rbacAccessibleFields, ...allAccessibleFields]));

  const showColumn = (value: string) => !accessibleFields.includes(value);

  // Defines the static and conditional columns present in the integrations table.
  const staticColumns: TableColumnConfig<TableData>[] = [
    {
      id: 'integration',
      Header: 'Integration',
      accessor: 'integration',
      minWidth: 150,
      sortType: 'string',
      filterConfig: { type: 'text' },
      sticky: 'left',
    },
    {
      id: 'tier',
      Header: 'Tier',
      accessor: 'tier',
      minWidth: 100,
      sortType: 'string',
      filterConfig: { type: 'radio' },
      omit: showColumn('tier'),
    },
    {
      id: 'type',
      Header: 'Type',
      accessor: 'type',
      minWidth: 150,
      sortType: 'string',
      filterConfig: { type: 'radio' },
      cellConfig: { element: 'Chip', variant: (type: string) => getTypeColor(type) },
    },
    {
      id: 'gaDate',
      Header: 'GA Date',
      accessor: 'gaDate',
      width: 150,
      sortType: 'date',
    },
    {
      id: 'lastUpdated',
      Header: 'Last Updated',
      accessor: 'lastUpdated',
      width: 150,
      sortType: 'date',
      omit: showColumn('lastUpdated'),
    },
  ];

  // Defines the dynamic columns based on rbac for the user.
  const dynamicColumns: TableColumnConfig<TableData>[] = accessibleFields
    ?.filter((field) => field !== 'tier' && field !== 'lastUpdated')
    ?.map((field) => {
      return {
        id: field,
        Header: replaceHeaderText(buildCapability(field).formatted),
        minWidth: 180,
        accessor: field,
        filterConfig: { type: 'radio' },
        cellRenderer: (value) =>
          value === Confirmation.YES
            ? Icon({ name: 'confirm-badge', color: 'success' })
            : Icon({ name: 'error-badge', color: 'error' }),
      };
    });

  const integrationsTableColumns = [...staticColumns, ...dynamicColumns];

  /**
   * Maps known column ids to accessor functions to extract data from integrations.
   */
  const predefinedAccessors: Record<string, (integration: Integration) => string> = {
    integration: (integration) => (integration?.displayName ? integration?.displayName : ''),
    tier: (integration) =>
      integration?.tier?.value && integration.tier.value !== 'TBD'
        ? buildCapability(integration.tier.value).formatted
        : '-',
    type: (integration) => (integration?.connectionType ? buildCapability(integration.connectionType).formatted : ''),
    gaDate: (integration) => (integration?.gaReleaseDate ? cleanDate(integration?.gaReleaseDate) : ''),
    lastUpdated: (integration) => (integration?.lastUpdated?.value ? cleanDate(integration?.lastUpdated?.value) : ''),
  };

  /**
   * Provides a default accessor for dynamic integration fields by checking
   * whether the given column ID matches any capability defined on the integration.
   *
   * This implementation precomputes all capability keys into a Set for efficient
   * O(1) membership lookups, making it more performant when resolving multiple
   * columns per integration.
   *
   * @param {Integration} integration - The integration record containing capabilities.
   * @param {string} colId - The column ID to check (expected to be in camelCase format).
   * @returns {Confirmation} Returns `Confirmation.YES` if the integration supports
   *                         the capability corresponding to the column ID,
   *                         otherwise returns `Confirmation.NO`.
   */

  const defaultAccessor = (integration: Integration, colId: string) => {
    const camelCaseSet = new Set(
      integration?.capabilities
        ?.filter((cap) => cap && typeof cap.dataType === 'string' && typeof cap.operation === 'string')
        ?.map((cap) =>
          cap && typeof cap.dataType === 'string' && typeof cap.operation === 'string'
            ? buildCapability(cap.dataType, cap?.operation).camelCase
            : ''
        )
    );
    return camelCaseSet?.has(colId) ? Confirmation.YES : Confirmation.NO;
  };

  // Constructs the table data by mapping integrations to rows based on column definitions.
  const integrationsTableData: TableData[] =
    integrations
      ?.filter((integration) => integration?.vertical?.toLowerCase() === normalizedSelectedVertical || isAllVertical)
      ?.map((integration) => {
        const rowData = {} as TableData;
        integrationsTableColumns?.forEach((column) => {
          const accessor = predefinedAccessors[column.id];
          rowData[column.id] = accessor ? accessor(integration) : defaultAccessor(integration, column.id);
        });
        return rowData;
      }) ?? [];

  return { integrationsTableColumns, integrationsTableData };
};

/**
 * Generates a mapping from user roles to accessible fields based on integration metadata and capabilities.
 * @param {IntegrationsMetadata} integrationsMetadata Metadata about integrations.
 * @param {Integration[]} integrations List of integrations with capabilities.
 * @returns {Record<string, string[]>} A map where keys are role names and values are arrays of accessible field strings.
 */
export const getCapabilitiesRbac = (
  integrationsMetadata?: IntegrationsMetadata,
  integrations?: Integration[]
): Record<string, string[]> => {
  const tempRoleCapabilities: Record<string, Set<string>> = {};

  if (!Array.isArray(integrations)) {
    return {};
  }

  /**
   * Safely assigns a capability to multiple roles, initializing sets as needed.
   * @param {(string | undefined)[]} roles Array of roles to assign the capability to.
   * @param {string} capability The capability string to assign.
   */
  const assignCapabilityToRoles = (roles: (string | undefined)[], capability: string) => {
    for (const role of roles) {
      if (!role) continue;
      const normalizedRole = role.toLowerCase();
      if (!tempRoleCapabilities[normalizedRole]) {
        tempRoleCapabilities[normalizedRole] = new Set();
      }
      tempRoleCapabilities[normalizedRole].add(capability);
    }
  };

  // Defines top-level fields in integrationsMetadata to process for rbac
  const topLevelFields: { key: keyof IntegrationsMetadata; name: string }[] = [
    { key: 'tier', name: 'tier' },
    { key: 'lastUpdated', name: 'lastUpdated' },
  ];

  for (const field of topLevelFields) {
    const fieldData = integrationsMetadata?.[field.key];
    if (fieldData && 'accessibleByRoles' in fieldData && Array.isArray(fieldData?.accessibleByRoles)) {
      const rolesWithAccess = [...fieldData.accessibleByRoles, fieldData?.owner?.toLowerCase()];
      assignCapabilityToRoles(rolesWithAccess, field.name);
    }
  }

  // Handle dynamic capabilities from integrations
  for (const integration of integrations) {
    if (Array.isArray(integration.capabilities)) {
      for (const capability of integration.capabilities) {
        if (!capability?.dataType || !capability?.operation) continue;

        const { camelCase } = buildCapability(capability.dataType, capability.operation);
        const rolesWithAccess = [...(capability?.accessibleByRoles ?? []), capability?.owner?.toLowerCase()];
        assignCapabilityToRoles(rolesWithAccess, camelCase);
      }
    }
  }

  // Converts temporary sets into arrays to finalize the mapping of roles to accessible fields
  const roleCapabilitiesMap: Record<string, string[]> = {};
  for (const role in tempRoleCapabilities) {
    roleCapabilitiesMap[role] = [...tempRoleCapabilities[role]];
  }

  return roleCapabilitiesMap;
};

/**
 * Checks whether the user is authenticated.
 *
 * The function verifies authentication by:
 * - Reading the `isUserAuthenticated` state from the integrations dashboard store and auth helper.
 * - Ensuring both `token` and `user` exist in localStorage.
 *
 * @returns {boolean} `true` if the user is authenticated, otherwise `false`.
 */
export const checkIsUserAuthenticated = (): boolean => {
  const { isUserAuthenticated } = useIntegrationsDashboardStore.getState();

  const token = localStorageHelper.get('token');
  const user = localStorageHelper.get('user');
  return isUserAuthenticated && !!token && !!user && authnClient.isUserAuthenticated();
};

//TODO : Delete this when API is ready -  this is just to simulate some delay for mock Data
export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Checks whether a given user role has access to a specific section based on RBAC (Role-Based Access Control) rules.
 * @param {RoleBasedValue<string>} [section] - The section object containing access rules such as roles and owner.
 * @param {keyof typeof UserRole} [userRole] - The current user's role to check access for.
 * @returns {boolean} - Returns `true` if the user has access (either included in `accessibleByRoles`, matches the `owner`, or if `UserRole.ALL` is allowed); otherwise returns `false`.
 */
export const checkRbacAccess = (section?: RoleBasedValue<string>, userRole?: keyof typeof UserRole): boolean => {
  if (!section || !userRole) return false;

  const { accessibleByRoles = [], owner } = section;
  const rolesSet = new Set(accessibleByRoles);

  return rolesSet.has(UserRole.ALL) || rolesSet.has(userRole) || owner === userRole;
};

/**
 * Converts a time string in minutes (e.g. "1660m") into
 * hours, days, or weeks depending on the value.
 *
 * @param {string} timeStr - Time string in minutes, e.g. "1660m"
 * @returns {string} - Converted time string (e.g. "27.7h", "1.2d", "2w")
 */
export const convertMinutes = (timeStr: string): string => {
  const minutes = parseFloat(timeStr);

  if (isNaN(minutes)) return '-';

  const minsInHour = 60;
  const hoursInDay = 24;
  const daysInWeek = 7;

  if (minutes < minsInHour) {
    return `${minutes}m`;
  }

  const hours = minutes / minsInHour;
  if (hours < hoursInDay) {
    return `${hours.toFixed(1)}h`;
  }

  const days = hours / hoursInDay;
  if (days < daysInWeek) {
    return `${days.toFixed(1)}d`;
  }

  const weeks = days / daysInWeek;
  return `${weeks.toFixed(1)}w`;
};

/**
 * Builds configuration and data for both "Entities" and "Writebacks" tables
 * based on the provided integrations metadata, capabilities, and current integration context.
 *
 * This function processes the integration configuration to determine which entities
 * support read synchronization (Sync) and which support writeback (Writeback) capabilities.
 * It also attaches appropriate status icons and frequency information for display.
 *
 * @param {IntegrationsMetadata} [integrationsMetadata] - Optional metadata describing all integrations.
 * @param {Integration[]} [integrations] - Optional list of integration configurations containing capabilities.
 * @param {string} [currentIntegration] - Optional identifier for the current integration (case and space insensitive).
 * @param {keyof typeof UserRole} [userRole] - Optional user role used when building integration tables.
 *
 * @returns {{
 *   entitiesTableColumns: TableColumnConfig<TableData>[];
 *   entitiesTableData: TableData[];
 *   writebacksTableColumns: TableColumnConfig<TableData>[];
 *   writebacksTableData: TableData[];
 * }} Returns column definitions and data for both the Entities and Writebacks tables.
 *
 */
export const buildEntitiesWritebacksTable = (
  integrationsMetadata?: IntegrationsMetadata,
  integrations?: Integration[],
  currentIntegration?: string,
  userRole?: keyof typeof UserRole
): {
  entitiesTableColumns: TableColumnConfig<TableData>[];
  entitiesTableData: TableData[];
  writebacksTableColumns: TableColumnConfig<TableData>[];
  writebacksTableData: TableData[];
} => {
  const normalizedIntegration = currentIntegration?.replace(/\s+/g, '').toLowerCase();

  const { integrationsTableData } = buildIntegrationsTable(integrationsMetadata, integrations, userRole, Verticals.ALL);

  const currentIntegrationData =
    integrationsTableData?.find(
      (intz) => intz?.integration?.replace(/\s+/g, '').toLowerCase() === normalizedIntegration
    ) ?? {};

  const currentIntegrationCapabilities =
    integrations?.find((intz) => intz?.displayName?.replace(/\s+/g, '').toLowerCase() === normalizedIntegration)
      ?.capabilities ?? [];

  const entries = Object.entries(currentIntegrationData);

  const entitiesTableData: TableData[] = [];
  const writebacksTableData: TableData[] = [];

  const formattedCapabilities = new Map(
    currentIntegrationCapabilities.map((cap) => {
      const name = buildCapability(cap.dataType ?? '', cap.operation ?? '').formatted;
      return [name, cap];
    })
  );

  for (const [key, value] of entries) {
    const capability = buildCapability(key).formatted;
    const label = replaceHeaderText(capability);
    const isConfirmed = value === Confirmation.YES;
    const icon = Icon({
      name: isConfirmed ? 'confirm-badge' : 'error-badge',
      color: isConfirmed ? 'success' : 'error',
    });

    if (label.endsWith('Sync')) {
      const matchedCap = formattedCapabilities.get(capability);
      const frequency = convertMinutes(matchedCap?.syncTiming?.syncFrequencyInMinutes ?? '') ?? '-';

      entitiesTableData.push({
        entity: label,
        read: icon,
        frequency,
        value: isConfirmed ? Confirmation.YES : Confirmation.NO,
      });
    } else if (label.endsWith('Writeback')) {
      writebacksTableData.push({
        writeback: label,
        integrated: icon,
        value: isConfirmed ? Confirmation.YES : Confirmation.NO,
      });
    }
  }

  const entitiesTableColumns: TableColumnConfig<TableData>[] = [
    { id: 'entity', Header: 'Entity', accessor: 'entity' },
    { id: 'read', Header: 'Read', accessor: 'read' },
    { id: 'frequency', Header: 'Frequency', accessor: 'frequency' },
  ];

  const writebacksTableColumns: TableColumnConfig<TableData>[] = [
    { id: 'writeback', Header: 'Writeback', accessor: 'writeback' },
    { id: 'integrated', Header: 'Integrated', accessor: 'integrated' },
  ];

  return {
    entitiesTableColumns,
    entitiesTableData,
    writebacksTableColumns,
    writebacksTableData,
  };
};
